import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import BookingInfor from './components/BookingInfor';
import OrderTimeLine from './components/OrderTimeLine';
import { useStore } from '@/hooks/useStore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { formatDateTime } from '@/utils/datetime';
import { DISPPLAY_MOMENT_YMD_HM, ekDomain, ORDER_STATUS_CMS, ROUTES } from '@/configs/constants';
import { CalenderIcon } from '@/components/icon/Calender';
import classNames from 'classnames';
import PaymentInfor from './components/PaymentInfor';
import Notes from './components/Notes';
import { isPathEk } from '@/utils/routes';
import AnimateButton from '@/components/general/button-custom/AnimateButton';

export default observer(function ReservationDetail() {
    // hooks
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        reservationStore: { reservation, getReservationCMSDetail, getPaymentHistory, paymentHistory, cleanDetail }
    } = useStore();

    // lifecycle
    useEffect(() => {
        if (id) {
            getReservationCMSDetail({ id });
            getPaymentHistory({ id });
        }

        return () => {
            cleanDetail();
        };
    }, [id]);

    // function
    const handleClickBack = () => {
        navigate(isEk ? `/${ekDomain}/${ROUTES.reservationManagement.href}` : `${ROUTES.reservationManagement.href}`);
    };

    return (
        <Suspense fallback={<div>{t('words_title.loading')}</div>}>
            <div className='relative'>
                <div className='flex flex-col gap-4 md:mt-0 point-600:mt-12'>
                    <div className='flex flex-col text-fs-mid18 leading-lh-sma14 -top-[52px] point-600:absolute xsm:gap-2 gap-1'>
                        <div className='point-600:flex hidden gap-1 items-center flex-wrap'>
                            <div>
                                <label className='font-semibold'>
                                    {t('field.reservation.reservation_code')}
                                    :
                                    {' '}
                                </label>
                                <span className='font-medium'>{reservation?.information?.orderCodeSystem}</span>
                            </div>
                            {reservation?.information?.orderStatus != null ?
                                (
                                    <div className={classNames(`${ORDER_STATUS_CMS[reservation?.information?.orderStatus]?.color} ${ORDER_STATUS_CMS[reservation?.information?.orderStatus]?.bgColor} border ${ORDER_STATUS_CMS[reservation?.information?.orderStatus]?.border} ml-1 font-normal px-3 h-6 flex items-center text-[1rem] rounded-[0.25rem]`, 'font-medium')}>
                                        {t(`${ORDER_STATUS_CMS[reservation?.information?.orderStatus]?.key}`)}
                                    </div>
                                ) :
                                <p>--</p>}
                        </div>
                        <div className='point-600:flex hidden gap-1 items-end'>
                            <p>
                                <CalenderIcon height={19} />
                            </p>
                            <p className='text-black-1 leading-[1]'>{reservation?.information?.orderDate && formatDateTime(reservation?.information?.orderDate, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</p>
                        </div>
                        <AnimateButton
                            type='button'
                            outline
                            side='left'
                            contentComp={t('button.back_to_screen')}
                            parentClassName='h-[44px] w-full point-600:hidden flex gap-1'
                            className=' px-3 rounded-button'
                            textClassName='!text-[14px] !pl-6.5'
                            iconArrowClassName='size-[15px]'
                            onClick={handleClickBack}
                        />
                    </div>
                    <div className='grid point-940:grid-cols-3 grid-cols-1 gap-y-4 md:gap-6 point-600:mt-6'>
                        <div className='bg-background-3 rounded-base point-940:col-span-2 col-span-full'>
                            <div className='bg-background-3 rounded-base point-940:col-span-2 col-span-full'>
                                <div className={classNames(
                                    'p-4 rounded-t-base point-600:hidden flex justify-between gap-1 text-white items-center',
                                    `${ORDER_STATUS_CMS[reservation?.information?.orderStatus]?.bgLinear}`
                                )}
                                >
                                    <p className='flex flex-col gap-1'>
                                        <label className='text-fs-mid14'>
                                            {t('field.reservation.reservation_code')}
                                        </label>
                                        <span className='font-semibold text-fs-mid16'>{reservation?.information?.orderCodeSystem}</span>
                                    </p>
                                    {reservation?.information?.orderStatus != null ?
                                        (
                                            <div className={classNames('bg-[#FFFFFF33] font-semibold px-3 py-1 h-6 flex items-center text-fs-mid14 rounded-[0.25rem]')}>
                                                {t(`${ORDER_STATUS_CMS[reservation?.information?.orderStatus]?.key}`)}
                                            </div>
                                        ) :
                                        <p>--</p>}
                                </div>
                                <div className='flex gap-1 items-end text-fs-mid16 point-600:hidden p-4 pb-0'>
                                    <label className=''>
                                        <CalenderIcon height={19} />
                                    </label>
                                    <span className='leading-[1] text-black-1'>{reservation?.information?.orderDate && formatDateTime(reservation?.information?.orderDate, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</span>
                                </div>
                            </div>
                            <BookingInfor reservation={reservation} isForEND={false} />
                        </div>
                        <div className='col-span-full layout-card point-940:col-span-1'>
                            <OrderTimeLine
                                historyTime={reservation?.information?.historyTime || {}}
                                orderStatus={reservation?.information?.orderStatus}
                                isEnduser={false}
                                paymentHistory={paymentHistory}
                            />
                        </div>
                    </div>
                    <div className='layout-card'>
                        <PaymentInfor reservation={reservation} isForEnd={false} />
                    </div>
                    <div className='layout-card'>
                        <Notes />
                    </div>
                </div>
            </div>
        </Suspense>
    );
});
